import { action } from '@ember/object';
import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import type MarketSettingsTransactionMappingController from 'lumen/controllers/market/settings/transaction-mapping';
import type {
  TransactionMappingHierarchyCategory,
  TransactionMappingItem,
} from 'lumen/models';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import type { Transition as TransitionWithTo } from 'lumen/services/transition';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export interface TransactionMappingParams {}

export default class MarketSettingsTransactionMappingRoute extends Route {
  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;

  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  @service('total-revenue/myhotels')
  declare totalRevenueMyhotels: Services['total-revenue/myhotels'];
  @service declare transition: Services['transition'];

  beforeUnloadHandler: ((event: BeforeUnloadEvent) => void) | null = null;
  transitionHandler: ((transition: Transition) => void) | null = null;

  requestTransactionMappingHierarchyTask = task(
    { restartable: true },
    async () => {
      const marketId = this.hotelAndMarketService.market.id;

      try {
        return await this.store.query<TransactionMappingHierarchyCategory>(
          'transaction-mapping/hierarchy-category',
          {
            namespace: APIGATEWAY,
            typeKey: 'totalrevenue/mapping/hierarchy',
            market_id: marketId,
          },
        );
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  requestTransactionMappingItemsTask = task({ restartable: true }, async () => {
    const marketId = this.hotelAndMarketService.market.id;

    try {
      const mappingPromise = this.store.query<TransactionMappingItem>(
        'transaction-mapping/item',
        {
          namespace: APIGATEWAY,
          typeKey: 'totalrevenue/mapping',
          market_id: marketId,
        },
      );

      // Ensure myhotels data is loaded as mapping items have a relationship to a hotel
      const myHotelsPromise = this.totalRevenueMyhotels.myHotelsTaskInstance;
      const [mapping] = await Promise.all([mappingPromise, myHotelsPromise]);

      return mapping;
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert({
        text: getAPIErrorMessage(error, this.intl),
      });
      this.errorTracking.captureException(error);

      return null;
    }
  });

  beforeModel(transition: TransitionWithTo): void {
    if (!this.access.hasTransactionMapping) {
      this.transition.redirectToEnabledTab(transition);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/require-await
  async model() {
    const model = {
      mapping: this.requestTransactionMappingItemsTask,
      hierarchy: this.requestTransactionMappingHierarchyTask,
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    model.mapping.perform();
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    model.hierarchy.perform();

    return model;
  }

  resetController(
    controller: MarketSettingsTransactionMappingController,
    isExiting: boolean,
    transition: Transition,
  ): void {
    super.resetController(controller, isExiting, transition);

    if (isExiting && this.transitionHandler && this.beforeUnloadHandler) {
      this.router.off('routeWillChange', this.transitionHandler);
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);

      this.transitionHandler = null;
      this.beforeUnloadHandler = null;
    }
  }

  setupController(
    controller: MarketSettingsTransactionMappingController,
    model: unknown,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    this.transitionHandler = (t: Transition) => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.onRouteWillChange(controller, t);
    };

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    this.beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      this.onBeforeUnload(controller, event);
    };

    this.router.on('routeWillChange', this.transitionHandler);
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  @action onBeforeUnload(
    controller: MarketSettingsTransactionMappingController,
    event: BeforeUnloadEvent,
  ): void {
    if (controller.hasUnsavedChanges) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  @action
  async onRouteWillChange(
    controller: MarketSettingsTransactionMappingController,
    transition: Transition,
  ): Promise<void> {
    if (
      (transition as TransitionWithTo).to.name !==
        'market.settings.transaction-mapping' &&
      controller.hasUnsavedChanges
    ) {
      transition.abort();

      const isCancelConfirmed = await controller.showDiscardChangesAlert();

      if (isCancelConfirmed) {
        controller.discardChanges();
        transition.retry();
      }
    }
  }
}
